import React, { useState } from 'react';
import { GitHubConfig } from '../types';
import { api } from '../lib/api';
import {
  Key,
  Settings as SettingsIcon,
  ToggleLeft,
  ToggleRight,
  FolderGit2,
  Radio,
  Copy,
  CheckCircle2,
  Terminal,
  Bell,
  Send,
  MessageSquare,
  Sliders,
  FileCode,
  Cpu,
  ExternalLink
} from 'lucide-react';

interface SettingsProps {
  config: GitHubConfig;
  onSaveConfig: (updated: GitHubConfig) => void;
  onSimulateWebhook: (input: { owner: string; repo: string; prNumber: number; baseSha: string; headSha: string; title?: string; body?: string }) => Promise<{ message: string; prId: string; taskId: string }>;
  loading: boolean;
  currentUser?: any;
}

export function Settings({ config, onSaveConfig, onSimulateWebhook, loading, currentUser }: SettingsProps) {
  const [webhookSecret, setWebhookSecret] = useState(config.webhookSecret);
  const [autoReview, setAutoReview] = useState(config.autoReview);
  
  // Notification states
  const [slackEnabled, setSlackEnabled] = useState(config.notifications?.slack?.enabled ?? false);
  const [slackWebhookUrl, setSlackWebhookUrl] = useState(config.notifications?.slack?.webhookUrl ?? '');
  const [slackChannel, setSlackChannel] = useState(config.notifications?.slack?.channel ?? '#alerts');

  const [telegramEnabled, setTelegramEnabled] = useState(config.notifications?.telegram?.enabled ?? false);
  const [telegramBotToken, setTelegramBotToken] = useState(config.notifications?.telegram?.botToken ?? '');
  const [telegramChatId, setTelegramChatId] = useState(config.notifications?.telegram?.chatId ?? '');

  // Agent System Instructions & Rules (Nim CLI targets)
  const [agentInstructions, setAgentInstructions] = useState(config.agentInstructions ?? '');
  const [rulesFormat, setRulesFormat] = useState<'NIM.md' | 'nimrc' | 'skill_card'>(config.rulesFormat ?? 'NIM.md');

  // Navigation tab for 2-column layout
  const [activeTab, setActiveTab] = useState<'connection' | 'rules' | 'notifications' | 'sandbox' | 'access'>('connection');

  // Admin access control states
  const [usersList, setUsersList] = useState<any[]>([]);
  const [emailsList, setEmailsList] = useState<any[]>([]);
  const [newApprovedEmail, setNewApprovedEmail] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessSuccess, setAccessSuccess] = useState<string | null>(null);

  const fetchUsersAndEmails = async () => {
    if (currentUser?.role !== 'Admin') return;
    setUsersLoading(true);
    setAccessError(null);
    try {
      const [users, emails] = await Promise.all([
        api<any[]>('/api/admin/users'),
        api<any[]>('/api/admin/emails'),
      ]);
      setUsersList(users);
      setEmailsList(emails);
    } catch (err: any) {
      setAccessError(err?.message ?? 'Failed to load access control tables.');
    } finally {
      setUsersLoading(false);
    }
  };

  React.useEffect(() => {
    if (activeTab === 'access') {
      fetchUsersAndEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleAddApprovedEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newApprovedEmail) return;
    setAccessError(null);
    setAccessSuccess(null);
    try {
      const data = await api<{ message?: string; error?: string }>('/api/admin/emails', {
        method: 'POST',
        body: JSON.stringify({ email: newApprovedEmail }),
      });
      setAccessSuccess(data.message || 'Email pre-authorized successfully.');
      setNewApprovedEmail('');
      fetchUsersAndEmails();
    } catch (err: any) {
      setAccessError(err?.message ?? 'Failed to authorize email.');
    }
  };

  const handleDeleteApprovedEmail = async (id: string) => {
    setAccessError(null);
    setAccessSuccess(null);
    try {
      const data = await api<{ message?: string }>(`/api/admin/emails/${id}`, { method: 'DELETE' });
      setAccessSuccess(data.message || 'Authorization removed.');
      fetchUsersAndEmails();
    } catch (err: any) {
      setAccessError(err?.message ?? 'Failed to remove authorization.');
    }
  };

  // Webhook Simulator state — dispatches a real pull_request event through the
  // pipeline against an existing PR at immutable base/head commits.
  const [simOwner, setSimOwner] = useState('');
  const [simRepo, setSimRepo] = useState('');
  const [simPrNumber, setSimPrNumber] = useState('');
  const [simBaseSha, setSimBaseSha] = useState('');
  const [simHeadSha, setSimHeadSha] = useState('');
  const [simDispatching, setSimDispatching] = useState(false);
  const [simResult, setSimResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const [saving, setSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await onSaveConfig({
        webhookSecret,
        autoReview,
        notifications: {
          slack: {
            enabled: slackEnabled,
            webhookUrl: slackWebhookUrl,
            channel: slackChannel
          },
          telegram: {
            enabled: telegramEnabled,
            botToken: telegramBotToken,
            chatId: telegramChatId
          }
        },
        agentInstructions,
        rulesFormat
      });
      // Secrets: stop showing the just-typed plaintext. Reflect "now set" as
      // the mask so the field matches what a fresh GET would return.
      if (webhookSecret) setWebhookSecret('***');
      if (slackWebhookUrl) setSlackWebhookUrl('***');
      if (telegramBotToken) setTelegramBotToken('***');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyWebhookUrl = () => {
    const url = `${window.location.origin}/api/webhook/github`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const triggerWebhookSim = async () => {
    const prNumber = Number(simPrNumber);
    if (!simOwner || !simRepo || !Number.isInteger(prNumber) || prNumber <= 0 || !simBaseSha || !simHeadSha) {
      setSimResult({ ok: false, message: 'Owner, repo, PR number, base SHA, and head SHA are all required.' });
      return;
    }
    setSimDispatching(true);
    setSimResult(null);
    try {
      const data = await onSimulateWebhook({ owner: simOwner, repo: simRepo, prNumber, baseSha: simBaseSha, headSha: simHeadSha });
      setSimResult({ ok: true, message: `Dispatched — taskId ${data?.taskId ?? '(unknown)'}` });
    } catch (err: any) {
      setSimResult({ ok: false, message: err?.message ?? 'Dispatch failed.' });
    } finally {
      setSimDispatching(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-8 min-h-[520px]" id="settings-view-grid">
      {/* Settings Navigation Sidebar */}
      <div className="w-full md:w-56 shrink-0 flex flex-col gap-0.5 min-w-0" id="settings-sidebar-nav">
        <div className="text-[10px] font-sans font-bold text-[rgba(55,53,47,0.5)] uppercase tracking-wider px-2 mb-2">
          Settings Menu
        </div>
        
        <button
          type="button"
          onClick={() => setActiveTab('connection')}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] text-xs font-medium font-sans transition text-left ${
            activeTab === 'connection'
              ? 'bg-[rgba(55,53,47,0.08)] text-[#37352F]'
              : 'text-[#37352F] hover:bg-[rgba(55,53,47,0.04)]'
          }`}
          id="tab-btn-connection"
        >
          <FolderGit2 className={`w-[14px] h-[14px] shrink-0 ${activeTab === 'connection' ? 'text-[#37352F]' : 'text-[rgba(55,53,47,0.5)]'}`} />
          <span className="truncate">GitHub Connection</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('rules')}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] text-xs font-medium font-sans transition text-left ${
            activeTab === 'rules'
              ? 'bg-[rgba(55,53,47,0.08)] text-[#37352F]'
              : 'text-[#37352F] hover:bg-[rgba(55,53,47,0.04)]'
          }`}
          id="tab-btn-rules"
        >
          <Cpu className={`w-[14px] h-[14px] shrink-0 ${activeTab === 'rules' ? 'text-[#37352F]' : 'text-[rgba(55,53,47,0.5)]'}`} />
          <span className="truncate">Nim Rules</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('notifications')}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] text-xs font-medium font-sans transition text-left ${
            activeTab === 'notifications'
              ? 'bg-[rgba(55,53,47,0.08)] text-[#37352F]'
              : 'text-[#37352F] hover:bg-[rgba(55,53,47,0.04)]'
          }`}
          id="tab-btn-notifications"
        >
          <Bell className={`w-[14px] h-[14px] shrink-0 ${activeTab === 'notifications' ? 'text-[#37352F]' : 'text-[rgba(55,53,47,0.5)]'}`} />
          <span className="truncate">Notifications</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('sandbox')}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] text-xs font-medium font-sans transition text-left ${
            activeTab === 'sandbox'
              ? 'bg-[rgba(55,53,47,0.08)] text-[#37352F]'
              : 'text-[#37352F] hover:bg-[rgba(55,53,47,0.04)]'
          }`}
          id="tab-btn-sandbox"
        >
          <Terminal className={`w-[14px] h-[14px] shrink-0 ${activeTab === 'sandbox' ? 'text-[#37352F]' : 'text-[rgba(55,53,47,0.5)]'}`} />
          <span className="truncate">Webhook Sandbox</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('access')}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-[4px] text-xs font-medium font-sans transition text-left ${
            activeTab === 'access'
              ? 'bg-[rgba(55,53,47,0.08)] text-[#37352F]'
              : 'text-[#37352F] hover:bg-[rgba(55,53,47,0.04)]'
          }`}
          id="tab-btn-access"
        >
          <Key className={`w-[14px] h-[14px] shrink-0 ${activeTab === 'access' ? 'text-[#37352F]' : 'text-[rgba(55,53,47,0.5)]'}`} />
          <span className="truncate">Access Control</span>
        </button>
      </div>

      {/* Settings Content Pane */}
      <div className="flex-1 bg-white border border-[rgba(55,53,47,0.09)] rounded-lg overflow-hidden" id="settings-content-pane">
        
        {activeTab === 'connection' && (
          <div className="animate-fadeIn">
            <div className="p-6 border-b border-[rgba(55,53,47,0.09)] bg-[#F7F7F5] flex items-center gap-3">
              <div className="p-2.5 bg-white text-[#37352F] rounded-[4px] border border-[rgba(55,53,47,0.09)]">
                <FolderGit2 className="w-5 h-5 text-[#2383E2]" />
              </div>
              <div>
                <h2 className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">GitHub Connection Settings</h2>
              </div>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-6">
              {/* GitHub App status */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-[rgba(55,53,47,0.6)] uppercase tracking-wider font-mono">GitHub App Authentication</label>
                <div className={`flex items-center gap-2 rounded-[4px] border px-4 py-2.5 text-xs ${config.appConfigured ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span className="font-mono">
                    {config.appConfigured
                      ? `${config.appBotName ?? 'nixor-nim[bot]'} — configured`
                      : 'Not configured'}
                  </span>
                </div>
                <p className="text-[11px] text-[rgba(55,53,47,0.6)] leading-relaxed">
                  Nim authenticates as the {config.appBotName ?? 'nixor-nim[bot]'} GitHub App. Set <code className="font-mono">GITHUB_APP_ID</code> (var) + <code className="font-mono">GITHUB_PRIVATE_KEY</code> (secret) on the Worker, then install the app on your repos/orgs to fetch projects and post reviews as the bot.
                </p>
              </div>

              {/* Webhook Secret */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-[rgba(55,53,47,0.6)] uppercase tracking-wider font-mono">Webhook Secret</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="whsec_..."
                    value={webhookSecret}
                    onChange={e => setWebhookSecret(e.target.value)}
                    className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-4 py-2 text-[#37352F] text-xs font-mono placeholder-[rgba(55,53,47,0.3)] outline-none transition pl-10"
                  />
                  <Key className="w-4 h-4 text-[rgba(55,53,47,0.4)] absolute left-3 top-2.5" />
                </div>
                <p className="text-[11px] text-[rgba(55,53,47,0.6)] leading-relaxed">
                  Must match the GitHub App webhook secret. Verifies inbound webhook signatures (HMAC-SHA256).
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end pt-4 border-t border-[rgba(55,53,47,0.09)]">
                <button
                  type="submit"
                  disabled={loading || saving}
                  className="bg-[#2383E2] hover:bg-[#1C69B5] text-white disabled:opacity-50 px-5 py-2 rounded-[4px] text-xs font-semibold shadow-xs transition"
                >
                  {saving ? 'Saving...' : 'Save Connection'}
                </button>
              </div>
            </form>

            {/* Admin Quick Install Help Card */}
            <div className="mx-6 mb-6 p-5 bg-gradient-to-br from-violet-50/80 to-purple-50/50 border border-purple-100 rounded-lg space-y-3 shadow-xs">
              <div className="flex items-center gap-2 text-purple-950">
                <div className="p-1.5 bg-purple-600 rounded text-white flex items-center justify-center">
                  <Terminal className="w-3.5 h-3.5" />
                </div>
                <h4 className="text-xs font-bold font-sans">
                  ⚡ Administrator GitHub Integration Quick Start
                </h4>
              </div>
              
              <p className="text-[11px] text-slate-600 leading-relaxed font-sans">
                This dashboard configuration is restricted to administrators. You can quickly integrate, install, or update this bot on any of your organisations or personal repos using the pre-configured GitHub App link below:
              </p>

              <div className="p-3 bg-white border border-purple-100 rounded-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="space-y-1">
                  <div className="text-[10px] uppercase font-mono font-bold text-slate-400">Target Integration App</div>
                  <div className="text-xs font-mono font-bold text-purple-700">nixor-nim-bot</div>
                </div>
                <a 
                  href="https://github.com/settings/apps/nixor-nim-bot"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold px-3 py-1.5 rounded text-[11px] transition duration-150 shadow-sm"
                >
                  <span>Install Nixor Nim Bot</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div className="text-[11px] text-slate-500 font-sans list-decimal list-inside space-y-1 bg-slate-50/50 p-2.5 rounded border border-slate-100">
                <div className="font-semibold text-slate-600 mb-1">How to connect:</div>
                <div>1. Click <strong className="text-purple-700">Install Nixor Nim Bot</strong> and authenticate.</div>
                <div>2. Install it to your preferred organisations/repositories.</div>
                <div>3. On the Worker, set <strong className="text-purple-700">GITHUB_APP_ID</strong> (var) + <strong className="text-purple-700">GITHUB_PRIVATE_KEY</strong> (<code className="font-mono">wrangler secret put</code>), then paste your <strong className="text-purple-700">Webhook Secret</strong> above and <strong className="text-purple-700">Save Connection</strong>.</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="animate-fadeIn">
            <div className="p-6 border-b border-[rgba(55,53,47,0.09)] bg-[#F7F7F5] flex items-center gap-3">
              <div className="p-2.5 bg-white text-[#37352F] rounded-[4px] border border-[rgba(55,53,47,0.09)]">
                <Cpu className="w-5 h-5 text-[#2383E2]" />
              </div>
              <div>
                <h2 className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">Nim CLI Agentic Rules</h2>
              </div>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-6">
              {/* Target Compilation Formats Choice */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-[#37352F] uppercase font-mono tracking-wide">
                  Target CLI Package Format (Rule Packaging)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setRulesFormat('NIM.md')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-[4px] border text-center transition ${
                      rulesFormat === 'NIM.md'
                        ? 'bg-[rgba(35,131,226,0.06)] border-[#2383E2] text-[#2383E2] font-bold'
                        : 'bg-white border-[rgba(55,53,47,0.09)] hover:bg-[#FAF9F6] text-[rgba(55,53,47,0.6)]'
                    }`}
                  >
                    <FileCode className="w-4 h-4" />
                    <span className="text-xs font-bold font-mono">NIM.md</span>
                    <span className="text-[9px] opacity-80 font-sans font-normal">Developer Rules File</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRulesFormat('nimrc')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-[4px] border text-center transition ${
                      rulesFormat === 'nimrc'
                        ? 'bg-[rgba(35,131,226,0.06)] border-[#2383E2] text-[#2383E2] font-bold'
                        : 'bg-white border-[rgba(55,53,47,0.09)] hover:bg-[#FAF9F6] text-[rgba(55,53,47,0.6)]'
                    }`}
                  >
                    <Terminal className="w-4 h-4" />
                    <span className="text-xs font-bold font-mono">.nimrc</span>
                    <span className="text-[9px] opacity-80 font-sans font-normal">Global JSON Schema</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRulesFormat('skill_card')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-[4px] border text-center transition ${
                      rulesFormat === 'skill_card'
                        ? 'bg-[rgba(35,131,226,0.06)] border-[#2383E2] text-[#2383E2] font-bold'
                        : 'bg-white border-[rgba(55,53,47,0.09)] hover:bg-[#FAF9F6] text-[rgba(55,53,47,0.6)]'
                    }`}
                  >
                    <Sliders className="w-4 h-4" />
                    <span className="text-xs font-bold font-mono">SKILL CARD</span>
                    <span className="text-[9px] opacity-80 font-sans font-normal">Markdown Skill Card</span>
                  </button>
                </div>
              </div>

              {/* Markdown Guidelines Area */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="block text-[10px] font-bold text-[#37352F] uppercase font-mono tracking-wide">
                    System Instructions & Rules (Markdown format)
                  </label>
                  <button
                    type="button"
                    onClick={() => setAgentInstructions(`# Nim Agent Rules

## System Role
You are an autonomous Senior Staff Engineer reviewing code changes in Pull Requests. Your priority is security, correctness, and performance.

## Core Directives
1. Security Focus: Look out for authorization bypasses, SQL injection, and concurrent race conditions.
2. Code Refactoring: Always provide fully-typed TypeScript solutions and self-contained replacement patches.
3. Micro-Optimizations: Flag database lock scopes, missing dependency arrays in React hooks, or unhandled exceptions.

## Environment Targets
- Build Command: \`npm run build\`
- Test Command: \`npm run test\`
- Lint Command: \`tsc --noEmit\``)}
                    className="text-[10px] text-[#2383E2] hover:underline font-mono focus:outline-none font-semibold"
                  >
                    Reset to Recommended Default
                  </button>
                </div>
                
                <textarea
                  rows={9}
                  value={agentInstructions}
                  onChange={e => setAgentInstructions(e.target.value)}
                  placeholder="Enter system instructions, linter commands, and test targets for the agent here..."
                  className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] p-4 text-[#37352F] text-xs font-mono outline-none resize-y leading-relaxed shadow-inner"
                />
              </div>

              {/* Realtime Packaged Compilation Preview */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-[rgba(55,53,47,0.6)] uppercase font-mono tracking-wide">
                  ⚡ Real-time Package Compiler Preview (Targeting Nim CLI)
                </label>
                
                <div className="bg-[#1C1C1A] text-[#F3F3F3] border border-[#2B2B29] rounded-[4px] p-4 space-y-3 font-mono text-[11px] shadow-lg leading-relaxed relative overflow-hidden">
                  <div className="flex items-center justify-between border-b border-[#2B2B29] pb-2 text-[10px] text-[#91918E]">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      <span className="ml-1 text-[#C4C4C2]">
                        {rulesFormat === 'NIM.md' ? 'workspace/NIM.md' : rulesFormat === 'nimrc' ? 'workspace/.nimrc' : 'workspace/skills/custom-rule.md'}
                      </span>
                    </span>
                    <span className="bg-[#2B2B29] px-2 py-0.5 rounded-[3px] text-[9px] uppercase font-bold text-emerald-400">Ready</span>
                  </div>

                  <div className="max-h-48 overflow-y-auto space-y-2 whitespace-pre-wrap select-text pr-2 scrollbar-thin scrollbar-thumb-gray-800">
                    {rulesFormat === 'NIM.md' && (
                      <div className="text-slate-300">
                        <span className="text-[#91918E]"># Compiled developer guides for Nim CLI</span>
                        <br />
                        {agentInstructions || '# Empty configuration. Type above to generate rules.'}
                      </div>
                    )}

                    {rulesFormat === 'nimrc' && (
                      <div className="text-yellow-200 text-[10px]">
                        <span>{JSON.stringify({
                          "$schema": "https://nimreviewer.com/schema/rc.json",
                          "agent": {
                            "systemInstructions": agentInstructions.split('\n').filter(l => l.trim().length > 0),
                            "cli_mode": "autonomous",
                            "allow_patching": true
                          },
                          "project": {
                            "lint_command": "npm run lint",
                            "test_command": "npm run test",
                            "build_command": "npm run build"
                          }
                        }, null, 2)}</span>
                      </div>
                    )}

                    {rulesFormat === 'skill_card' && (
                      <div>
                        <span className="text-indigo-400 font-bold">---</span>
                        <br />
                        <span className="text-emerald-400">name:</span> <span className="text-white">nim-review-compliance</span>
                        <br />
                        <span className="text-emerald-400">description:</span> <span className="text-white">Autonomous rule enforcement for static analysis compliance</span>
                        <br />
                        <span className="text-indigo-400 font-bold">---</span>
                        <br />
                        <br />
                        <span className="text-[#91918E]"># Custom Verification Card Guidelines</span>
                        <br />
                        <span className="text-slate-300">{agentInstructions}</span>
                      </div>
                    )}
                  </div>

                  <div className="text-[9px] text-[#636361] pt-1 border-t border-[#2B2B29] flex justify-between">
                    <span>Compilation Status: Ok</span>
                    <span>Size: {new Blob([agentInstructions]).size} bytes</span>
                  </div>
                </div>
              </div>

              {/* Autopilot toggle */}
              <div className="border-t border-[rgba(55,53,47,0.09)] pt-6 flex items-center justify-between">
                <div className="space-y-0.5">
                  <h4 className="text-sm font-semibold text-[#37352F]">Continuous Integration Autopilot</h4>
                  <p className="text-xs text-[rgba(55,53,47,0.6)]">Run review feedback loops automatically upon webhook delivery.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAutoReview(!autoReview)}
                  className="text-[#37352F] transition focus:outline-none"
                >
                  {autoReview ? (
                    <ToggleRight className="w-10 h-10 text-[#2383E2]" />
                  ) : (
                    <ToggleLeft className="w-10 h-10 text-[rgba(55,53,47,0.3)]" />
                  )}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end pt-4 border-t border-[rgba(55,53,47,0.09)]">
                <button
                  type="submit"
                  disabled={loading || saving}
                  className="bg-[#2383E2] hover:bg-[#1C69B5] text-white disabled:opacity-50 px-5 py-2 rounded-[4px] text-xs font-semibold shadow-xs transition"
                >
                  {saving ? 'Saving...' : 'Save Agent Rules'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="animate-fadeIn">
            <div className="p-6 border-b border-[rgba(55,53,47,0.09)] bg-[#F7F7F5] flex items-center gap-3">
              <div className="p-2.5 bg-white text-[#37352F] rounded-[4px] border border-[rgba(55,53,47,0.09)]">
                <Bell className="w-5 h-5 text-[#2383E2]" />
              </div>
              <div>
                <h2 className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">Admin Notification Targets</h2>
              </div>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Slack Channel Config */}
                <div className="border border-[rgba(55,53,47,0.09)] rounded-[4px] p-4 space-y-3 bg-[#FAF9F6]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded bg-[#4A154B]"></span>
                      <span className="text-xs font-semibold font-sans text-[#37352F]">Slack Channel Notification</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSlackEnabled(!slackEnabled)}
                      className="text-xs font-medium transition focus:outline-none"
                    >
                      {slackEnabled ? (
                        <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-[3px] border border-emerald-200 text-[10px] font-bold uppercase">Active</span>
                      ) : (
                        <span className="text-[rgba(55,53,47,0.6)] bg-[#F1F1EF] px-2 py-0.5 rounded-[3px] border border-[rgba(55,53,47,0.09)] text-[10px] font-bold uppercase">Disabled</span>
                      )}
                    </button>
                  </div>

                  {slackEnabled && (
                    <div className="space-y-3 pt-1 animate-fadeIn">
                      <div className="space-y-1">
                        <label className="block text-[9px] font-bold text-[rgba(55,53,47,0.6)] uppercase font-mono">Incoming Webhook URL</label>
                        <input 
                          type="text"
                          placeholder="https://hooks.slack.com/services/..."
                          value={slackWebhookUrl}
                          onChange={e => setSlackWebhookUrl(e.target.value)}
                          className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[9px] font-bold text-[rgba(55,53,47,0.6)] uppercase font-mono">Slack Channel (Optional)</label>
                        <input 
                          type="text"
                          placeholder="#alerts"
                          value={slackChannel}
                          onChange={e => setSlackChannel(e.target.value)}
                          className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Telegram Bot Config */}
                <div className="border border-[rgba(55,53,47,0.09)] rounded-[4px] p-4 space-y-3 bg-[#FAF9F6]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Send className="w-3.5 h-3.5 text-[#0088cc]" />
                      <span className="text-xs font-semibold font-sans text-[#37352F]">Telegram Group Notification</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTelegramEnabled(!telegramEnabled)}
                      className="text-xs font-medium transition focus:outline-none"
                    >
                      {telegramEnabled ? (
                        <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-[3px] border border-emerald-200 text-[10px] font-bold uppercase">Active</span>
                      ) : (
                        <span className="text-[rgba(55,53,47,0.6)] bg-[#F1F1EF] px-2 py-0.5 rounded-[3px] border border-[rgba(55,53,47,0.09)] text-[10px] font-bold uppercase">Disabled</span>
                      )}
                    </button>
                  </div>

                  {telegramEnabled && (
                    <div className="space-y-3 pt-1 animate-fadeIn">
                      <div className="space-y-1">
                        <label className="block text-[9px] font-bold text-[rgba(55,53,47,0.6)] uppercase font-mono">Telegram Bot Token</label>
                        <input 
                          type="password"
                          placeholder="123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ"
                          value={telegramBotToken}
                          onChange={e => setTelegramBotToken(e.target.value)}
                          className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[9px] font-bold text-[rgba(55,53,47,0.6)] uppercase font-mono">Target Chat ID (Group or Personal)</label>
                        <input 
                          type="text"
                          placeholder="e.g. -100123456789"
                          value={telegramChatId}
                          onChange={e => setTelegramChatId(e.target.value)}
                          className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end pt-4 border-t border-[rgba(55,53,47,0.09)]">
                <button
                  type="submit"
                  disabled={loading || saving}
                  className="bg-[#2383E2] hover:bg-[#1C69B5] text-white disabled:opacity-50 px-5 py-2 rounded-[4px] text-xs font-semibold shadow-xs transition"
                >
                  {saving ? 'Saving...' : 'Save Notifications'}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'sandbox' && (
          <div className="animate-fadeIn">
            <div className="p-6 border-b border-[rgba(55,53,47,0.09)] bg-[#F7F7F5] flex items-center gap-3">
              <div className="p-2.5 bg-white text-[#37352F] rounded-[4px] border border-[rgba(55,53,47,0.09)]">
                <Terminal className="w-5 h-5 text-[#2383E2]" />
              </div>
              <div>
                <h2 className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">Webhook Sandbox Simulator</h2>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Webhook Endpoint Info */}
              <div className="border border-[rgba(55,53,47,0.09)] rounded-[4px] p-6 space-y-4 bg-[#FAF9F6]/30">
                <div className="flex items-center gap-2 text-[#37352F]">
                  <Radio className="w-4 h-4 text-[#2383E2]" />
                  <span className="text-[10px] font-bold font-mono uppercase tracking-wider">Live Webhook Endpoint</span>
                </div>
                <p className="text-xs text-[rgba(55,53,47,0.6)] leading-relaxed">
                  Configure this exact payload URL in your GitHub repository setting to receive true automated pull request payloads.
                </p>
                <div className="flex items-center gap-2 bg-white border border-[rgba(55,53,47,0.09)] p-2.5 rounded-[4px] text-xs font-mono text-[#37352F]">
                  <span className="truncate flex-1">/api/webhook/github</span>
                  <button 
                    onClick={handleCopyWebhookUrl}
                    className="text-[rgba(55,53,47,0.4)] hover:text-[#37352F] p-1 focus:outline-none"
                    title="Copy absolute URL"
                  >
                    {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {/* Payload Generator */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Sliders className="w-4 h-4 text-[#2383E2]" />
                  <span className="text-xs font-semibold font-sans text-[#37352F]">Target PR (real repo + immutable refs)</span>
                </div>

                <p className="text-[11px] text-[rgba(55,53,47,0.6)] leading-relaxed">
                  Dispatches a synthetic <code className="font-mono">pull_request</code> event through the live pipeline. Point it at a real open PR and provide its exact base and head commit SHAs.
                </p>

                <div className="space-y-3 pt-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase">Repo Owner</label>
                      <input
                        type="text"
                        aria-label="Repo owner"
                        placeholder="e.g. niko0xdev"
                        value={simOwner}
                        onChange={e => setSimOwner(e.target.value)}
                        className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase">Repo Name</label>
                      <input
                        type="text"
                        aria-label="Repo name"
                        placeholder="e.g. nim-ai"
                        value={simRepo}
                        onChange={e => setSimRepo(e.target.value)}
                        className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase">PR Number</label>
                    <input
                      type="number"
                      min={1}
                      aria-label="PR number"
                      placeholder="e.g. 42"
                      value={simPrNumber}
                      onChange={e => setSimPrNumber(e.target.value)}
                      className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase">Base SHA</label>
                    <input
                      type="text"
                      aria-label="Base commit SHA"
                      placeholder="e.g. 0d4a2f1… (full 40-char SHA)"
                      value={simBaseSha}
                      onChange={e => setSimBaseSha(e.target.value)}
                      className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold font-mono text-[rgba(55,53,47,0.6)] uppercase">Head SHA</label>
                    <input
                      type="text"
                      aria-label="Head commit SHA"
                      placeholder="e.g. 3b626c4… (full 40-char SHA)"
                      value={simHeadSha}
                      onChange={e => setSimHeadSha(e.target.value)}
                      className="w-full bg-white border border-[rgba(55,53,47,0.09)] focus:border-[#2383E2] rounded-[4px] px-3 py-1.5 text-[#37352F] text-xs font-mono outline-none"
                    />
                  </div>

                  {simResult && (
                    <div className={`text-xs rounded-md p-2.5 flex items-start gap-2 ${simResult.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-rose-50 border border-rose-200 text-rose-700'}`}>
                      <span className="flex-1 font-mono">{simResult.message}</span>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={triggerWebhookSim}
                    disabled={simDispatching}
                    className="w-full bg-[#2383E2] hover:bg-[#1C69B5] disabled:opacity-50 text-white py-2.5 rounded-[4px] text-xs font-mono font-semibold transition mt-2 shadow-xs"
                    id="btn-simulate-webhook"
                  >
                    {simDispatching ? 'Dispatching…' : 'Dispatch Simulation Payload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'access' && (
          <div className="animate-fadeIn" id="settings-tab-access-panel">
            <div className="p-6 border-b border-[rgba(55,53,47,0.09)] bg-[#F7F7F5] flex items-center justify-between">
              <div className="flex items-center gap-3 text-left">
                <div className="p-2.5 bg-white text-[#37352F] rounded-[4px] border border-[rgba(55,53,47,0.09)] shrink-0">
                  <Key className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xs font-bold text-[#37352F] uppercase tracking-wider font-mono">User Access & Role Controls</h2>
                  <p className="text-[10px] text-slate-400 mt-0.5">Manage administrative emails and secure credentials across the platform.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold uppercase border ${
                  currentUser?.role === 'Admin' 
                    ? 'bg-purple-50 text-purple-700 border-purple-200' 
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  Role: {currentUser?.role || 'Reviewer'}
                </span>
              </div>
            </div>

            {currentUser?.role !== 'Admin' ? (
              <div className="p-12 text-center space-y-3">
                <div className="inline-flex p-3 bg-amber-50 rounded-full text-amber-600 border border-amber-100">
                  <Key className="w-6 h-6" />
                </div>
                <h3 className="text-sm font-semibold text-slate-800">Administrator Access Required</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
                  You are currently logged in as a <strong>Reviewer</strong>. Only <strong>Admin</strong> users are authorized to view and modify user rosters or approve email credentials.
                </p>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Status Banners */}
                {accessError && (
                  <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-md p-3 flex items-center gap-2 animate-fadeIn">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-600 shrink-0" />
                    <span className="flex-1 text-left">{accessError}</span>
                  </div>
                )}
                {accessSuccess && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded-md p-3 flex items-center gap-2 animate-fadeIn">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                    <span className="flex-1 text-left">{accessSuccess}</span>
                  </div>
                )}

                {/* TWO COLUMN GRID LAYOUT */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                  
                  {/* Column 1: Approved Admin Emails (Pre-authorizations) */}
                  <div className="space-y-4">
                    <div className="border border-slate-200 rounded-lg p-5 bg-slate-50/50 space-y-4">
                      <div className="space-y-1 text-left">
                        <h4 className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wide">1. Pre-Approved Admin Emails</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          Enter email addresses that are pre-authorized to join as <strong>Admin</strong>. Anyone registering with these emails will automatically acquire Admin permissions.
                        </p>
                      </div>

                      <form onSubmit={handleAddApprovedEmail} className="flex gap-2">
                        <input 
                          type="email"
                          required
                          placeholder="e.g. admin@acme.com"
                          value={newApprovedEmail}
                          onChange={e => setNewApprovedEmail(e.target.value)}
                          className="flex-1 bg-white border border-slate-200 focus:border-purple-500 rounded px-3 py-1.5 text-xs outline-none shadow-3xs"
                        />
                        <button 
                          type="submit"
                          className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-1.5 rounded text-xs transition shrink-0 shadow-3xs"
                        >
                          Add Email
                        </button>
                      </form>

                      {/* List of Approved Emails */}
                      <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide text-left">Approved Email List</p>
                        {emailsList.length === 0 ? (
                          <p className="text-xs text-slate-400 italic py-2 text-left">No pre-approved admin emails configured.</p>
                        ) : (
                          emailsList.map(item => (
                            <div key={item.id} className="flex items-center justify-between bg-white border border-slate-100 rounded p-2 text-xs shadow-3xs">
                              <span className="font-medium text-slate-700 truncate mr-2">{item.email}</span>
                              <button 
                                type="button"
                                onClick={() => handleDeleteApprovedEmail(item.id)}
                                className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50 transition font-medium"
                                title="Remove pre-authorization"
                              >
                                Delete
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Column 2: SSO user roster (provision in Keycloak) */}
                  <div className="space-y-4">
                    <div className="border border-slate-200 rounded-lg p-5 bg-slate-50/50 space-y-4">
                      <div className="space-y-1 text-left">
                        <h4 className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wide">2. Platform Users (SSO)</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          Users are provisioned in Keycloak (realm zworks). They appear here after first Sign in with Zworks. PLATFORM_ADMIN maps to Admin.
                        </p>
                      </div>

                      {/* Active User Roster */}
                      <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1 pt-1">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide text-left">Active Platform Users</p>
                        {usersList.length === 0 ? (
                          <p className="text-xs text-slate-400 italic py-2 text-left">No users found.</p>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {usersList.map(u => {
                              const label = u.email || u.username || u.name || u.id;
                              return (
                              <div key={u.id || label} className="flex items-center justify-between py-2 text-xs">
                                <div className="flex flex-col text-left">
                                  <span className="font-semibold text-slate-800">{label}</span>
                                  <span className="text-[10px] text-slate-400 truncate">{u.provider || 'sso'} · {u.role}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`text-[9px] px-1.5 py-0.2 rounded font-mono font-bold uppercase ${
                                    u.role === 'Admin'
                                      ? 'bg-purple-50 text-purple-600 border border-purple-100'
                                      : 'bg-blue-50 text-blue-600 border border-blue-100'
                                  }`}>{u.role}</span>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                    </div>
                  </div>

                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
